import React from 'react';
import { Target, Sparkles, TreePine, Wine, Heart } from 'lucide-react';

const GolfLifestyle = () => {
  const experiences = [
    {
      id: 'golf',
      label: 'Excellence sportive',
      title: 'Académie de Golf',
      description: 'Perfectionnez votre swing sur un parcours de classe mondiale, dessiné par l\'architecte de golf Alain Cnrjac.',
      image: 'https://images.pexels.com/photos/1325659/pexels-photo-1325659.jpeg',
      size: 'large',
    },
    {
      id: 'spa',
      label: 'Détente & Sérénité',
      title: 'Spa & Bien-être',
      description: 'Une expérience sensorielle unique au cœur d\'un écrin architectural conçu par Nicolas Duchateau.',
      image: 'https://images.unsplash.com/photo-1626293319219-02512b53b967?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzcGElMjB3ZWxsbmVzcyUyMHJlbGF4YXRpb24lMjBwb29sJTIwdHJvcGljYWx8ZW58MHx8fHwxNzc0MjgyNzY4fDA&ixlib=rb-4.1.0&q=85',
      size: 'small',
    },
    {
      id: 'sports',
      label: 'Vie active',
      title: 'Sports & Loisirs Nature',
      description: 'Tennis, Padel et sentiers de randonnée pour une vie active en famille au sein du domaine One Green Dev.',
      image: 'https://images.unsplash.com/photo-1763913771541-4d43d5c3bc93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB0ZW5uaXMlMjBwYWRlbCUyMGNvdXJ0JTIwdHJvcGljYWwlMjByZXNvcnR8ZW58MHx8fHwxNzc0Mjg5NjI0fDA&ixlib=rb-4.1.0&q=85',
      size: 'small',
    },
    {
      id: 'clubhouse',
      label: 'Art culinaire',
      title: 'Le Club House & Gastronomie',
      description: 'Savourez l\'excellence culinaire et des moments de partage face au coucher du soleil sur le 18ème trou.',
      image: 'https://images.pexels.com/photos/6048946/pexels-photo-6048946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      size: 'large',
    },
  ];

  const features = [
    {
      icon: Target,
      title: 'Académie de Golf',
      description: 'Parcours 9 trous par Alain Cnrjac.',
    },
    {
      icon: Sparkles,
      title: 'Spa & Bien-être',
      description: 'Relaxation au cœur du domaine.',
    },
    {
      icon: TreePine,
      title: 'Sports & Nature',
      description: 'Tennis, Padel, randonnée.',
    },
    {
      icon: Wine,
      title: 'Club House',
      description: 'Gastronomie & art de vivre.',
    },
  ];

  return (
    <section id="lifestyle" className="py-24 lg:py-32 bg-[#faf8f5] relative overflow-hidden" data-testid="lifestyle-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-montserrat text-[#c9a962] text-xs tracking-[0.4em] uppercase">
            L'Expérience Membre
          </span>
          <div className="w-16 h-px bg-[#c9a962] mx-auto my-6" />
          <h2 className="font-playfair text-4xl lg:text-6xl text-[#1a3c2a]">
            Un <span className="text-[#c9a962] italic">Art de Vivre</span>
          </h2>
        </div>

        {/* Experiences Bento Grid */}
        <div className="grid grid-cols-12 gap-4 lg:gap-5 mb-16">
          {experiences.map((exp) => (
            <div
              key={exp.id}
              className={`group relative overflow-hidden rounded-2xl ${
                exp.size === 'large'
                  ? 'col-span-12 md:col-span-7 aspect-[16/9]'
                  : 'col-span-12 md:col-span-5 aspect-[4/3]'
              }`}
              data-testid={`experience-card-${exp.id}`}
            >
              <img
                src={exp.image}
                alt={exp.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              {/* Gradient for readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f14]/90 via-[#0a1f14]/30 to-transparent" />

              {/* Text Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-7">
                <span className="font-montserrat text-[#c9a962] text-[10px] tracking-[0.3em] uppercase">
                  {exp.label}
                </span>
                <h3 className={`font-playfair text-white mt-1.5 ${
                  exp.size === 'large' ? 'text-xl lg:text-2xl' : 'text-lg lg:text-xl'
                }`}>
                  {exp.title}
                </h3>
                <p className={`font-montserrat text-white/60 text-sm leading-relaxed mt-2 ${
                  exp.size === 'large' ? 'max-w-md' : 'max-w-xs'
                }`}>
                  {exp.description}
                </p>
              </div>

              {/* Hover Border */}
              <div className="absolute inset-0 border-2 border-[#c9a962]/0 group-hover:border-[#c9a962]/30 rounded-2xl transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Quote Section */}
        <div className="bg-[#1a3c2a] rounded-2xl py-12 px-8 lg:px-16 mb-16 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-20 h-20 border-l border-t border-[#c9a962]/20 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-20 h-20 border-r border-b border-[#c9a962]/20 rounded-br-2xl" />

          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <Heart className="w-7 h-7 text-[#c9a962] mx-auto mb-5" />
            <blockquote className="font-playfair text-xl lg:text-2xl text-white italic leading-relaxed mb-4">
              "Bien plus qu'un golf, un art de vivre où chaque instant 
              est une invitation au prestige."
            </blockquote>
            <p className="font-montserrat text-white/40 text-xs tracking-widest uppercase">
              One Green Dev
            </p>
          </div>
        </div>

        {/* Features - Elegant Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {features.map((feature, index) => (
            <div key={index} className="group text-center bg-white rounded-xl p-6 lg:p-7 border border-transparent hover:border-[#c9a962]/20 hover:shadow-lg transition-all duration-500">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#1a3c2a]/5 group-hover:bg-[#c9a962]/10 flex items-center justify-center transition-colors duration-500">
                <feature.icon className="w-5 h-5 text-[#c9a962] stroke-[1.5]" />
              </div>
              <h3 className="font-playfair text-sm lg:text-base text-[#1a3c2a] mb-1.5">
                {feature.title}
              </h3>
              <div className="w-6 h-px bg-[#c9a962]/30 mx-auto mb-2" />
              <p className="font-montserrat text-xs text-gray-400 font-light leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GolfLifestyle;
